/**
 * /catalog — SOTA product catalog experience.
 *
 * Consumer-facing browse view: search, category filter, sort, grid of
 * real product cards (photo + label + price + lead + style match) with
 * click-to-add into the active project's room when one is selected.
 *
 * Import is decoupled — `/catalog-import` is the admin ingest surface;
 * /catalog is what the designer / client actually browses every day.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowDown, ArrowUp, Filter, Plus, Tag, CloudUpload } from "lucide-react";
import codegen from "../../../docs/ontology/codegen.json";
import { useActiveProjectGraph } from "../lib/useActiveProjectGraph";
import { photoForSkuNode } from "../data/imagery";
import { formatCompactMoney, formatMoney } from "../lib/i18n/format";
import { appendImportedCatalog, appendImportActivity, ingestSeedEntries, readImportedCatalog } from "../lib/catalogIngest";
import { addPlacement, mergeUserPlacements } from "../lib/userPlacements";
import type { GraphNode } from "../types/ontology";

// Auto-seed reads from codegen.json#demoSeedSkus — ontology is the source
// of truth for the demo dataset (CLAUDE.md §1: no hardcoded CSV in code).
// 8 real IKEA products (KLIPPAN, MALM, INGOLF, LINNMON/ADILS, PAX, BILLY,
// LACK, EKEDALEN) with their actual IKEA article numbers preserved as
// SKU.ikeaArticle for traceability.
import codegenSeed from "../../../docs/ontology/codegen.json";
interface SeedRow {
  id: string; label: string; category: string;
  price?: number; leadDays?: number; styleMatch?: number; vendor?: string;
  photoUrl?: string; modelUrl?: string;
  datasetId?: string; rowIndex?: number; ikeaArticle?: string;
}
const DEMO_SEED_SKUS: ReadonlyArray<SeedRow> = (codegenSeed.demoSeedSkus ?? []) as ReadonlyArray<SeedRow>;
const AUTOSEED_KEY = "foyrmind.catalogAutoSeeded.";

type SortKey = "name" | "price-asc" | "price-desc" | "lead-asc" | "style-desc";

function attrNum(n: GraphNode, key: string): number | null {
  const v = n.attrs[key];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  return null;
}
function attrStr(n: GraphNode, key: string): string | null {
  const v = n.attrs[key];
  return typeof v === "string" ? v : null;
}

// Read SKU.category enum from ontology — UI filters never hardcode the list.
const CATEGORIES: ReadonlyArray<string> = (() => {
  const fields = (codegen.fields ?? []) as ReadonlyArray<{ path: string; enum?: ReadonlyArray<string> }>;
  const cat = fields.find((f) => f.path === "SKU.category");
  return cat?.enum ?? [];
})();

export default function Catalog(): JSX.Element {
  const { graph: baseGraph, activeId } = useActiveProjectGraph();
  const [autoSeedTick, setAutoSeedTick] = useState(0);

  // First-visit auto-seed: if no catalog imported yet for this project,
  // materialize the 8-SKU IKEA demonstration set. Honest-flow: clears
  // via /catalog-import "Clear imported catalog". Real photos arrive
  // when designer ingests the actual Kaggle CSV from kg-5-ikea-sa.
  useEffect(() => {
    try {
      const seeded = window.localStorage.getItem(AUTOSEED_KEY + activeId);
      if (seeded === "true") return;
      const existing = readImportedCatalog(activeId);
      if (existing.length > 0) {
        window.localStorage.setItem(AUTOSEED_KEY + activeId, "true");
        return;
      }
      const result = ingestSeedEntries({
        source: "kaggle-csv",
        entries: DEMO_SEED_SKUS,
        projectId: activeId,
      });
      appendImportedCatalog(activeId, result.skus);
      appendImportActivity(activeId, result.importActivity, result.provenanceEdges);
      window.localStorage.setItem(AUTOSEED_KEY + activeId, "true");
      setAutoSeedTick((t) => t + 1);
    } catch (err) {
      // CLAUDE.md §5 — surface, never silently swallow.
      // eslint-disable-next-line no-console
      console.error("[Catalog] auto-seed failed", err);
    }
  }, [activeId]);

  // Merge user placements + imported catalog into one combined catalog.
  const graph = useMemo(() => {
    const withPlacements = mergeUserPlacements(baseGraph, activeId);
    const imported = readImportedCatalog(activeId);
    if (imported.length === 0) return withPlacements;
    const existingIds = new Set(withPlacements.nodes.map((n) => n.id));
    const fresh = imported.filter((n) => !existingIds.has(n.id));
    return { ...withPlacements, nodes: [...withPlacements.nodes, ...fresh] };
  }, [baseGraph, activeId, autoSeedTick]);

  const allSkus = useMemo(() => graph.nodes.filter((n) => n.kind === "SKU"), [graph.nodes]);
  const rooms = useMemo(() => graph.nodes.filter((n) => n.kind === "Room"), [graph.nodes]);

  const [query, setQuery] = useState<string>("");
  const [selectedCat, setSelectedCat] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>("name");
  const [targetRoomId, setTargetRoomId] = useState<string | null>(rooms[0]?.id ?? null);
  const [, setTick] = useState(0);

  // Compute typed counts per category for filter chips.
  const catCounts = useMemo<ReadonlyMap<string, number>>(() => {
    const m = new Map<string, number>();
    for (const sku of allSkus) {
      const cat = attrStr(sku, "category") ?? "Item";
      m.set(cat, (m.get(cat) ?? 0) + 1);
    }
    return m;
  }, [allSkus]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = allSkus;
    if (selectedCat !== null) {
      out = out.filter((s) => attrStr(s, "category") === selectedCat);
    }
    if (q.length > 0) {
      out = out.filter((s) =>
        s.label.toLowerCase().includes(q)
        || (attrStr(s, "vendor") ?? "").toLowerCase().includes(q)
        || (attrStr(s, "category") ?? "").toLowerCase().includes(q),
      );
    }
    const sorted = [...out];
    switch (sort) {
      case "name":        sorted.sort((a, b) => a.label.localeCompare(b.label)); break;
      case "price-asc":   sorted.sort((a, b) => (attrNum(a, "price") ?? Infinity) - (attrNum(b, "price") ?? Infinity)); break;
      case "price-desc":  sorted.sort((a, b) => (attrNum(b, "price") ?? -Infinity) - (attrNum(a, "price") ?? -Infinity)); break;
      case "lead-asc":    sorted.sort((a, b) => (attrNum(a, "leadDays") ?? Infinity) - (attrNum(b, "leadDays") ?? Infinity)); break;
      case "style-desc":  sorted.sort((a, b) => (attrNum(b, "styleMatch") ?? -Infinity) - (attrNum(a, "styleMatch") ?? -Infinity)); break;
    }
    return sorted;
  }, [allSkus, query, selectedCat, sort]);

  const totalValue = useMemo(() => filtered.reduce((s, x) => s + (attrNum(x, "price") ?? 0), 0), [filtered]);

  function handleAdd(sku: GraphNode): void {
    if (!targetRoomId) return;
    addPlacement(activeId, sku.id, targetRoomId);
    setTick((t) => t + 1);
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8 space-y-5">
      <header className="rounded-3xl border border-border/70 bg-gradient-to-br from-foyr/10 via-cream to-sea/5 px-6 py-6 overflow-hidden">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow inline-flex items-center gap-1.5">
              <Tag className="w-3 h-3" /> Product catalog
            </div>
            <h1 className="font-newyork text-[34px] mt-1 text-ink-950 leading-[1.04] tracking-tight">
              {allSkus.length} products. {CATEGORIES.length} categories. One typed graph.
            </h1>
            <p className="text-[12.5px] text-ink-700 mt-2 max-w-2xl">
              Every product is a typed <code className="font-mono">SKU</code> node with ontology-validated category,
              price, lead time, style match, vendor, photo + (when imported) 3D model URL.
              Click <Plus className="inline w-3 h-3 align-text-bottom" /> on any tile to add it to a room
              of <span className="font-medium text-ink-950">{graph.projectName}</span>.
            </p>
          </div>
          <Link
            to="/catalog-import"
            className="btn btn-ghost inline-flex items-center gap-1.5 text-[12px]"
            title="Bring real product data from Kaggle CSV / 3D-Front / Foyr Neo"
          >
            <CloudUpload className="w-3.5 h-3.5" /> Import more
          </Link>
        </div>
      </header>

      {/* Search + sort + room target row */}
      <section className="panel p-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 text-ink-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="search by name, vendor, category"
            className="flex-1 text-[12.5px] bg-transparent outline-none placeholder:text-ink-500"
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className="text-[11.5px] px-2 py-1 rounded-lg border border-border bg-cream"
          title="Sort the catalog"
        >
          <option value="name">Sort: name</option>
          <option value="price-asc">Price: low → high</option>
          <option value="price-desc">Price: high → low</option>
          <option value="lead-asc">Lead time: low → high</option>
          <option value="style-desc">Style match: high → low</option>
        </select>
        {rooms.length > 0 ? (
          <select
            value={targetRoomId ?? ""}
            onChange={(e) => setTargetRoomId(e.target.value || null)}
            className="text-[11.5px] px-2 py-1 rounded-lg border border-border bg-cream"
            title="Add-to-room target"
          >
            <option value="">Pick room to add into…</option>
            {rooms.map((r) => <option key={r.id} value={r.id}>Add to: {r.label}</option>)}
          </select>
        ) : null}
      </section>

      {/* Category chips — driven by ontology enum */}
      <section className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[10.5px] uppercase tracking-wider text-ink-500 inline-flex items-center gap-1.5">
          <Filter className="w-3 h-3" /> Category
        </span>
        <button
          type="button"
          onClick={() => setSelectedCat(null)}
          className={
            "pill text-[11px] border " +
            (selectedCat === null
              ? "bg-ink-950 text-cream border-ink-950"
              : "bg-cream border-border text-ink-700 hover:border-foyr/40")
          }
        >
          All ({allSkus.length})
        </button>
        {CATEGORIES.map((cat) => {
          const count = catCounts.get(cat) ?? 0;
          if (count === 0) return null;
          const active = selectedCat === cat;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCat(cat)}
              className={
                "pill text-[11px] border " +
                (active
                  ? "bg-ink-950 text-cream border-ink-950"
                  : "bg-cream border-border text-ink-700 hover:border-foyr/40")
              }
            >
              {cat} ({count})
            </button>
          );
        })}
      </section>

      {/* Total + summary row */}
      <div className="text-[11.5px] text-ink-500 tabular flex items-center gap-3 px-1">
        <span>{filtered.length} showing</span>
        <span className="text-ink-300">·</span>
        <span>Total {formatCompactMoney(totalValue)}</span>
        {sort === "price-asc" || sort === "price-desc" ? (
          <span className="inline-flex items-center gap-1 text-ink-500">
            {sort === "price-asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            price
          </span>
        ) : null}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="panel p-8 text-center text-ink-500 text-[13px]">
          No products match. Try clearing the filter or import a catalog at{" "}
          <Link to="/catalog-import" className="text-foyr-deep underline">/catalog-import</Link>.
        </div>
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {filtered.map((sku) => {
            const price = attrNum(sku, "price");
            const lead = attrNum(sku, "leadDays");
            const style = attrNum(sku, "styleMatch");
            const vendor = attrStr(sku, "vendor");
            const category = attrStr(sku, "category");
            const photo = photoForSkuNode(sku);
            const provSource = attrStr(sku, "_source");
            const provDataset = attrStr(sku, "_datasetId");
            return (
              <li key={sku.id} className="rounded-2xl border border-border/70 bg-cream overflow-hidden flex flex-col group hover:border-foyr/40 transition">
                <div className="relative h-32 bg-cream-dark/40">
                  <img
                    src={photo}
                    alt={sku.label}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      const el = e.currentTarget;
                      el.src = "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 80'%3E%3Crect width='100' height='80' fill='%23ece7dc'/%3E%3Ctext x='50' y='44' font-family='-apple-system,Inter,sans-serif' font-size='9' fill='%237a766f' text-anchor='middle'%3Ephoto pending%3C/text%3E%3C/svg%3E";
                    }}
                  />
                  {category ? (
                    <span className="absolute top-1.5 left-1.5 pill text-[9.5px] bg-cream/95 border border-border tabular">{category}</span>
                  ) : null}
                  {targetRoomId ? (
                    <button
                      type="button"
                      onClick={() => handleAdd(sku)}
                      className="absolute top-1.5 right-1.5 grid place-items-center w-7 h-7 rounded-full bg-cream/95 text-foyr-deep group-hover:bg-foyr group-hover:text-cream transition shadow-sm"
                      title={`Add to ${rooms.find((r) => r.id === targetRoomId)?.label ?? "room"}`}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  ) : null}
                </div>
                <div className="px-3 py-2.5 flex-1 flex flex-col gap-1">
                  <div className="text-[12.5px] font-medium text-ink-950 leading-tight line-clamp-2 min-h-[2.5em]">
                    {sku.label}
                  </div>
                  <div className="text-[10.5px] text-ink-500 tabular flex items-center gap-2 mt-auto">
                    {price !== null ? <span className="font-medium text-ink-950">{formatMoney(price)}</span> : null}
                    {lead !== null ? <span className={lead >= 60 ? "text-foyr-deep" : ""}>{lead}d</span> : null}
                    {style !== null ? <span>{Math.round(style * 100)}%</span> : null}
                  </div>
                  <div className="text-[10px] text-ink-500 tabular flex items-center gap-1.5 truncate">
                    {vendor ? <span>{vendor}</span> : null}
                    {provDataset ? <span className="text-foyr-deep">· {provDataset}</span> : null}
                    {provSource && !provDataset ? <span>· {provSource}</span> : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
